// Global
import {
  Field,
  Image as JSSImage,
  ImageField,
  useSitecoreContext,
} from '@sitecore-jss/sitecore-jss-nextjs';
import NextImage from 'lib/image/nextjs-image-override';
import Head from 'next/head';

// Lib
import { getEnum } from 'lib/utils/get-enum';
import useExperienceEditor from 'lib/utils/use-experience-editor';
import classNames from 'classnames';
import { Item } from '@sitecore-jss/sitecore-jss-nextjs';
import { LayoutValue, RatioTypes, maxhTypes, maxwTypes } from '.';
import { Caption } from '../Caption';
import useNormalMode from 'lib/utils/use-normal-mode';
import { isSvgUrl } from 'lib/utils/string-utils';
import { useTheme } from 'lib/context/ThemeContext';
import { removeCdnHostName } from 'lib/image/remove-cdn-hostname';

/**
 * JSS does not yet support Next Image in Exprience Editor
 * This component will switch between the two based on environment
 * which allows us to get the various performance benefits from Next Image
 *
 * Note that the images may display slightly differently in
 * Experience Editor as the JSS Image component doesn't have the same layout options
 */
export type ImageWrapperProps = {
  image?: ImageField;
  mobileImage?: ImageField;
  mobileFocusArea?: Item;
  caption?: Field<string>;
  hideCaption?: boolean;
  imageLayout?: LayoutValue;
  additionalDesktopClasses?: string;
  additionalMobileClasses?: string;
  ratio?: RatioTypes;
  maxH?: maxhTypes;
  maxW?: maxwTypes;
  focusArea?: string;
  priority?: boolean;
  generateSchemaMarkup?: boolean;
  alwaysUseFocusArea?: boolean;
};

const ImageWrapper = ({
  image,
  mobileImage,
  mobileFocusArea,
  caption,
  hideCaption = false,
  imageLayout = 'responsive',
  additionalDesktopClasses,
  additionalMobileClasses,
  ratio = 'picture',
  maxH = '',
  maxW = '',
  focusArea = 'center',
  priority,
  generateSchemaMarkup = true,
  alwaysUseFocusArea = false,
}: ImageWrapperProps): JSX.Element => {
  const { themeName } = useTheme();
  const isEE = useExperienceEditor();
  const isNormalMode = useNormalMode();
  const { sitecoreContext } = useSitecoreContext();
  // If we're in EE, we still want to render the image for editing, even when it's empty.
  if (!image?.value?.src && !isEE) {
    return <></>;
  }
  const imageValue = image?.value;
  const isSvg = !!imageValue && !!imageValue.src && isSvgUrl(imageValue.src);
  const baseUrl = sitecoreContext.extendedSiteInfo?.targetHostName
    ? 'https://' + sitecoreContext.extendedSiteInfo?.targetHostName
    : process.env.PUBLIC_URL;

  function urlUpdate(url: string): string {
    const sitecorePattern = /^https:\/\/edge\.sitecorecloud\.io\/andersencorporation-[^/]+\//;
    if (sitecorePattern.test(url)) {
      url = url.replace(sitecorePattern, '/-/');
    }
    const imageUrl = `${baseUrl}${checkUrlTLD(url)}`;
    return imageUrl;
  }

  function checkUrlTLD(url?: string): string | undefined {
    if (url?.includes('.ca') && !baseUrl?.includes('.ca')) {
      url = url.replace('.ca', '.com');
    }
    return url;
  }

  const ldJsonScriptImage = {
    '@context': 'https://schema.org',
    '@type': 'ImageObject',
    contentUrl: imageValue?.src ? urlUpdate(imageValue?.src) : imageValue?.src,
    creator: 'Andersen Windows',
  };

  if ((maxH || maxW) && !imageLayout) {
    imageLayout = 'responsive';
  }

  const imageCss = imageLayout == 'responsive' ? `aspect-${ratio} ${maxH} ${maxW}` : '';
  const mobileObjectPosition = focusArea != '' ? focusArea : getEnum<string>(mobileFocusArea) || '';

  if (imageLayout === 'responsive' || imageLayout === 'intrinsic') {
    return (
      <>
        {generateSchemaMarkup && themeName === 'aw' && (
          <Head>
            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{ __html: JSON.stringify(ldJsonScriptImage) }}
            />
          </Head>
        )}
        <div className={classNames('relative hidden md:block', imageCss, additionalDesktopClasses)}>
          {isNormalMode ? (
            <NextImage
              src={`${checkUrlTLD(imageValue?.src)}`}
              alt={`${imageValue?.alt}`}
              width={`${imageValue?.width}`}
              height={`${imageValue?.height}`}
              layout={imageLayout}
              priority={priority}
              unoptimized={isSvg}
              objectFit={alwaysUseFocusArea ? 'none' : undefined}
              objectPosition={alwaysUseFocusArea ? mobileObjectPosition : ''}
              overrideSrc={`${removeCdnHostName(imageValue?.src)}`}
            />
          ) : (
            <JSSImage field={image} priority={priority} />
          )}
        </div>
        <MobileImage
          image={image}
          mobileImage={mobileImage}
          imageLayout={imageLayout}
          additionalMobileClasses={additionalMobileClasses}
          mobileObjectPosition={mobileObjectPosition}
          priority={priority}
          alwaysUseFocusArea={alwaysUseFocusArea}
        />
        {!hideCaption && <Caption caption={caption} />}
      </>
    );
  } else {
    const ratioCss = `aspect-${ratio} ${maxH} ${maxW}`;
    return (
      <>
        <div
          className={classNames(
            'relative mx-auto hidden md:block',
            ratioCss,
            additionalDesktopClasses
          )}
        >
          {isNormalMode ? (
            <NextImage
              src={`${imageValue?.src}`}
              alt={`${imageValue?.alt}`}
              layout="fill"
              objectFit={ratio === 'portrait' ? 'contain' : 'cover'}
              objectPosition={alwaysUseFocusArea ? mobileObjectPosition : undefined}
              priority={priority}
              unoptimized={isSvg}
              overrideSrc={`${removeCdnHostName(imageValue?.src)}`}
            />
          ) : (
            <JSSImage
              field={image}
              style={{
                objectFit: ratio === 'portrait' ? 'contain' : 'cover',
              }}
              priority={priority}
            />
          )}
        </div>
        <MobileImage
          image={image}
          mobileImage={mobileImage}
          imageLayout={imageLayout}
          additionalMobileClasses={additionalMobileClasses}
          mobileObjectPosition={mobileObjectPosition}
          priority={priority}
          alwaysUseFocusArea={alwaysUseFocusArea}
        />
        {!hideCaption && <Caption caption={caption} />}
      </>
    );
  }
};

export default ImageWrapper;

const MobileImage = ({
  image,
  mobileImage,
  imageLayout = 'responsive',
  additionalMobileClasses,
  mobileObjectPosition,
  priority,
  alwaysUseFocusArea,
}: ImageWrapperProps & { mobileObjectPosition: string }): JSX.Element => {
  const isNormalMode = useNormalMode();

  if (mobileImage?.value?.src) {
    const isSvg = isSvgUrl(mobileImage.value.src);
    return (
      <div className={classNames('relative block w-full md:hidden', additionalMobileClasses)}>
        {isNormalMode ? (
          <NextImage
            src={`${mobileImage.value.src}`}
            alt={`${mobileImage.value.alt}`}
            width={imageLayout !== 'fill' ? `${mobileImage.value.width}` : undefined}
            height={imageLayout !== 'fill' ? `${mobileImage.value.height}` : undefined}
            layout={imageLayout}
            objectPosition={alwaysUseFocusArea ? mobileObjectPosition : 'center'}
            objectFit={imageLayout === 'responsive' ? 'contain' : 'cover'}
            priority={priority}
            unoptimized={isSvg}
            overrideSrc={`${removeCdnHostName(mobileImage.value.src)}`}
          />
        ) : (
          <JSSImage
            field={image}
            style={{
              objectPosition: alwaysUseFocusArea ? mobileObjectPosition : 'center',
              objectFit: imageLayout === 'responsive' ? 'contain' : 'cover',
            }}
            priority={priority}
          />
        )}
      </div>
    );
  } else {
    if (image?.value?.src) {
      const isSvg = isSvgUrl(image.value.src);
      return (
        <div className={classNames('relative w-full md:hidden', additionalMobileClasses)}>
          {isNormalMode ? (
            <NextImage
              src={`${image.value.src}`}
              alt={`${image.value.alt}`}
              width={imageLayout !== 'fill' ? `${image.value.width}` : undefined}
              height={imageLayout !== 'fill' ? `${image.value.height}` : undefined}
              layout={imageLayout}
              objectPosition={mobileObjectPosition}
              objectFit="cover"
              priority={priority}
              unoptimized={isSvg}
              overrideSrc={`${removeCdnHostName(image.value.src)}`}
            />
          ) : (
            <JSSImage
              field={image}
              style={{
                objectPosition: mobileObjectPosition,
                objectFit: 'cover',
              }}
              priority={priority}
            />
          )}
        </div>
      );
    }
  }

  return <></>;
};
